/**
 * @module hooks/useSettingsDraft
 * @description Settings 层 — Confirm/Cancel 流程 + onChange 写 Draft + convert() 调用
 */

import { useCallback, useMemo } from 'react';
import { useDispatch } from 'umi';
import type { IColumnInfo } from '@dbl/core/dataSource';
import { ChartType, type TChartType } from '@dbl/core/chart';
import { useChartSettingsDispatch, useChartSettingsSelector } from '../../context';
import { convert } from '../../services/convert';
import { CSActionType } from '../../types';
import { confirmSyncToQB } from '../../confirmSyncQB';
import {
  computeChangedSettingKeys,
  computeNeedsRecalculation,
} from './settingsRecalcPolicy';

/** onConfirm 返回值 */
export interface IConfirmResult {
  payload: Record<string, unknown>;
  /** 是否需要触发后端重算（有修改的字段不在 notCalculate 集合中） */
  needsRecalculation: boolean;
}

export interface ISettingsDraftQuery {
  chartType: TChartType;
  columnDimensions: IColumnInfo[];
  columnMetrics: IColumnInfo[];
}

export function useSettingsDraft(query: ISettingsDraftQuery) {
  const dispatch = useChartSettingsDispatch();
  const dvaDispatch = useDispatch();
  const { chartType, columnDimensions, columnMetrics } = query;
  const base = useChartSettingsSelector(s => s.base);
  const draft = useChartSettingsSelector(s => s.draft);
  const hasDraft = useChartSettingsSelector(s => s.hasDraft);

  const onChange = useCallback(
    (key: string, value: unknown) => {
      dispatch({
        type: CSActionType.UPDATE_DRAFT,
        payload: { [key]: value },
      });
    },
    [dispatch],
  );

  const changedKeys = useMemo(
    () =>
      computeChangedSettingKeys({
        base: base as Record<string, unknown>,
        draft: draft as Record<string, unknown>,
        hasDraft,
      }),
    [draft, base, hasDraft],
  );

  const needsRecalculation = computeNeedsRecalculation(changedKeys);

  const onConfirm = useCallback(
    (formDelKey: Record<string, boolean> = {}): IConfirmResult => {
      const payload = convert({
        chartType,
        base,
        draft,
        hasDraft,
        formDelKey,
      });

      const commitBase = { ...base, ...draft };
      for (const key of Object.keys(hasDraft)) {
        if (!(key in draft)) {
          delete commitBase[key];
        }
      }

      dispatch({ type: CSActionType.COMMIT_DRAFT });

      confirmSyncToQB({
        commitBase,
        columnDimensions,
        columnMetrics,
        dvaDispatch,
      });

      return {
        payload,
        needsRecalculation,
      };
    },
    [
      chartType,
      base,
      draft,
      hasDraft,
      dispatch,
      dvaDispatch,
      columnDimensions,
      columnMetrics,
      needsRecalculation,
    ],
  );

  const onCancel = useCallback(() => {
    dispatch({ type: CSActionType.RESET_DRAFT });
  }, [dispatch]);

  return { onChange, onConfirm, onCancel };
}

